
import React from 'react';

const PaintsCoatingsSection: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Risks of Paints and Coatings
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Managing hazards linked to paints and coatings—such as solvents, urethanes, and epoxies—is essential due to their prevalence in various industries and potential health risks from chemical exposure.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Chemical Hazards in Paint Products
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                These materials, including volatile organic compounds (VOCs) and isocyanates, pose significant dangers through inhalation and direct contact. Monitoring these risks in industrial settings is crucial, as these substances can affect employee health adversely, leading to conditions ranging from respiratory irritation to more severe reactions like asthma.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                    <i className="ri-drop-line text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Volatile Organic Compounds (VOCs)</h4>
                    <p className="text-gray-700">Can cause headaches, dizziness, and long-term organ damage</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                    <i className="ri-flask-line text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Isocyanates</h4>
                    <p className="text-gray-700">Highly reactive chemicals that can trigger asthma and respiratory sensitization</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                    <i className="ri-test-tube-line text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Heavy Metals</h4>
                    <p className="text-gray-700">Lead, chromium, and other metals in specialty coatings</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
                <h4 className="font-semibold text-blue-800 mb-2">Our Monitoring Approach:</h4>
                <p className="text-blue-700">
                  Our industrial hygiene services ensure that workplaces adhere to established safety standards, protecting workers from immediate and long-term health issues. Monitoring air quality for VOCs and particulates in areas where these substances are used is a primary method to maintain safe exposure levels.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Control Measures & Safety Protocols
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Implementing control measures based on air quality data, such as improved ventilation and using safer materials like water-based paints, will reduce potential health risks.
              </p>

              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-wind-line text-green-600 mr-3"></i>
                    Engineering Controls
                  </h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Local exhaust ventilation systems</li>
                    <li>• Spray booth installations</li>
                    <li>• Air filtration systems</li>
                  </ul>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-shield-user-line text-blue-600 mr-3"></i>
                    Personal Protective Equipment
                  </h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Respiratory protection programs</li>
                    <li>• Chemical-resistant gloves</li>
                    <li>• Protective clothing and eyewear</li>
                  </ul>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-book-open-line text-purple-600 mr-3"></i>
                    Training & Education
                  </h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Safe handling procedures</li>
                    <li>• Health hazard awareness</li>
                    <li>• Emergency response protocols</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-700 mb-8 text-lg max-w-4xl mx-auto leading-relaxed">
              Employee education on the safe handling of paints and coatings, along with the consistent use of personal protective equipment (PPE), is fundamental to ensuring safety in the workplace. Regular training on the health hazards associated with these substances and the correct usage of PPE helps safeguard against exposure.
            </p>
            <a 
              href="#contact-form" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Get A Painting Protocol Assessment
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaintsCoatingsSection;
